import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ClickableElement from '../ClickableElement';
import { colors, getHoverColor } from '@/styles';

/**
 * ToggleSwitch component
 *
 * @version 1.0.0
 * @param {import('./ToggleSwitch').ToggleSwitchProps} props
 */
function ToggleSwitch({
  className,
  defaultState,
  backgroundColor,
  thumbColor,
  size,
  onClick,
  disabled,
  isActive,
  dataTestId,
  isRebrand,
}) {
  const [active, toggleActive] = useState(defaultState);

  useEffect(() => {
    if (isActive !== active) {
      toggleActive(isActive);
    }
    // We actually only want to trigger a rerender if 'isActive' changes, not 'active'
    /* eslint-disable-next-line */
  }, [isActive]);

  /** @type {import('../ClickableElement').ClickableElementProps['onClick']} */
  const handleClick = (event) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    toggleActive(!active);
    onClick(!active);
  };

  return (
    <ClickableElement
      className={`${className} ${active ? 'active' : ''}`}
      onClick={handleClick}
      aria-disabled={disabled}
      tabIndex={disabled ? '-1' : '1'}
    >
      <span {...(dataTestId ? { 'data-testid': dataTestId } : {})} />
    </ClickableElement>
  );
}

ToggleSwitch.propTypes = {
  /** The className to be injected onto the component */
  className: PropTypes.string.isRequired,
  /** A function to emit back to a parent component calling this ToggleSwitch */
  onClick: PropTypes.func.isRequired,
  /** The starting state for the toggleSwitch, on or off */
  defaultState: PropTypes.bool,
  /** The list of colors options available for the background */
  backgroundColor: PropTypes.string,
  /** The list of colors options available for the thumb */
  thumbColor: PropTypes.string,
  /** The size of the component's height in rems, width ratio of 2 is maintained */
  size: PropTypes.number,
  /** Can the switch be interacted with */
  disabled: PropTypes.bool,
  /** provides a way of controlling state outside the compoennt */
  isActive: PropTypes.bool,
  /** allows you to define the  test-id from the outside for rtl */
  dataTestId: PropTypes.string,
  /** specifies if it should be rebrand */
  isRebrand: PropTypes.bool,
};

ToggleSwitch.defaultProps = {
  defaultState: false,
  backgroundColor: colors.taosSky,
  thumbColor: colors.white,
  size: 1,
  disabled: false,
  isActive: false,
  dataTestId: '',
  isRebrand: false,
};

const styledToggleSwitch = styled(ToggleSwitch)`
  border-radius: 40rem;
  display: flex;
  background: ${(props) => (props.isRebrand ? colors.mist : colors.silverSand)};
  transition: 0.4s;

  &:focus {
    outline: none;
  }

  span {
    position: relative;
    left: 0;
    width: 50%;
    border-radius: 50%;
    transform: scale(0.75);
    transition: 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &.active span {
    left: 50%;
  }

  ${(props) => `
    width: ${props.size * 2}rem;
    height: ${props.size}rem;

    span {
      background: ${props.thumbColor};
    }

    ${(() => {
      if (props.disabled) {
        return `
          cursor: default;
          pointer-events: none;

          span {
            background: ${props.isRebrand ? colors.disableGrey : colors.whiteSmoke};
          }
        `;
      }
      return `
        cursor: pointer;

        &:focus {
          box-shadow: 0 0 0 1px ${props.isRebrand ? colors.electricBlue : colors.taosSkyLight};
        }

        &:hover {
          background: ${
            props.isRebrand ? getHoverColor(colors.mist) : getHoverColor(colors.silverSand)
          };
        }

        &.active {
          background: ${props.backgroundColor};

          &:hover {
            background: ${getHoverColor(props.backgroundColor)};
          }
        }
      `;
    })()}
  `}
`;

export default styledToggleSwitch;
